/**
 * Production-ready logging utility
 * Only logs in development, silently ignores in production
 */

type LogLevel = "debug" | "info" | "warn" | "error";

interface LogOptions {
  context?: string;
  metadata?: Record<string, any>;
}

class Logger {
  private isProduction = import.meta.env.PROD;
  private isDevelopment = import.meta.env.DEV;

  private formatMessage(
    level: LogLevel,
    message: string,
    options?: LogOptions,
  ): string {
    const timestamp = new Date().toISOString();
    const context = options?.context ? `[${options.context}]` : "";
    const metadata = options?.metadata
      ? ` ${JSON.stringify(options.metadata)}`
      : "";
    return `${timestamp} [${level.toUpperCase()}]${context} ${message}${metadata}`;
  }

  debug(message: string, options?: LogOptions): void {
    if (this.isDevelopment) {
      console.debug(this.formatMessage("debug", message, options));
    }
  }

  info(message: string, options?: LogOptions): void {
    if (this.isDevelopment) {
      console.info(this.formatMessage("info", message, options));
    }
  }

  warn(message: string, options?: LogOptions): void {
    if (this.isDevelopment) {
      console.warn(this.formatMessage("warn", message, options));
    }
  }

  error(message: string, error?: Error | unknown, options?: LogOptions): void {
    if (this.isDevelopment) {
      const errorInfo =
        error instanceof Error
          ? { message: error.message, stack: error.stack }
          : error;

      const enhancedOptions = {
        ...options,
        metadata: {
          ...options?.metadata,
          error: errorInfo,
        },
      };

      console.error(this.formatMessage("error", message, enhancedOptions));
    }
  }

  // Development-only methods
  group(label: string): void {
    if (this.isDevelopment) {
      console.group(label);
    }
  }

  groupEnd(): void {
    if (this.isDevelopment) {
      console.groupEnd();
    }
  }

  table(data: any): void {
    if (this.isDevelopment) {
      console.table(data);
    }
  }

  time(label: string): void {
    if (this.isDevelopment) {
      console.time(label);
    }
  }

  timeEnd(label: string): void {
    if (this.isDevelopment) {
      console.timeEnd(label);
    }
  }

  // Production error reporting (could be extended to send to error tracking service)
  reportError(
    message: string,
    error?: Error | unknown,
    metadata?: Record<string, any>,
  ): void {
    if (this.isProduction && error instanceof Error) {
      // In production, you could send errors to Sentry, LogRocket, etc.
      // For now, we'll silently handle the error
      // Example: Sentry.captureException(error, { extra: metadata });
    }
  }
}

// Export singleton instance
export const logger = new Logger();

// Legacy console replacement for easy migration
export const devConsole = {
  log: (message?: any, ...optionalParams: any[]) => {
    if (import.meta.env.DEV) {
      console.log(message, ...optionalParams);
    }
  },
  warn: (message?: any, ...optionalParams: any[]) => {
    if (import.meta.env.DEV) {
      console.warn(message, ...optionalParams);
    }
  },
  error: (message?: any, ...optionalParams: any[]) => {
    if (import.meta.env.DEV) {
      console.error(message, ...optionalParams);
    }
  },
  debug: (message?: any, ...optionalParams: any[]) => {
    if (import.meta.env.DEV) {
      console.debug(message, ...optionalParams);
    }
  },
  info: (message?: any, ...optionalParams: any[]) => {
    if (import.meta.env.DEV) {
      console.info(message, ...optionalParams);
    }
  },
};

export default logger;
